import React, { useState, useEffect, useMemo } from "react";

// ANT DESIGN
import {
  Input,
  Row,
  Card,
  Table,
  Spin,
  Button,
  Space,
  Form,
  Pagination,
  Select,
} from "antd";
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";

// PROJECT IMPORTS
import { useNavigate } from "react-router-dom";
import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import { BOM } from "../../../services/definedMethod";
import cnt from "../../../services/definedConstant";
import { connect, useDispatch } from "react-redux";
import { rightAction } from "../../../redux/actions";
import DownloadTableButtons from "../../../components/layouts/enhancement/DownloadTableButtons";
import { apiConfig, labelConfig, staticConfig } from "./financialInstrument.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import ChevronDown from "@untitled-ui/icons-react/build/esm/ChevronDown";
import "../../../assets/styles/Bond.css";

const ManageSecurityInstruction = (props) => {
  // Redux + navigation
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData || {};

  // Local state
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [securityList, setSecurityList] = useState([]);
  const [originalList, setOriginalList] = useState([]);
  const [loading, setLoading] = useState(false);

  const pageSize = 10;

  // Columns (Option A: instrumentName, shortName, instrumentType)
  const columns = [
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Instrument Name
          <div>
            <ArrowDownOutlined style={{ fontSize: 15, color: "gray" }} />
          </div>
        </div>
      ),
      dataIndex: "instrumentName",
      sorter: (a, b) =>
        String(a.instrumentName || "").localeCompare(String(b.instrumentName || "")),
    },
    {
      title: "Short Name",
      dataIndex: "shortName",
      sorter: (a, b) =>
        String(a.shortName || "").localeCompare(String(b.shortName || "")),
    },
    {
      title: "Instrument Type",
      dataIndex: "instrumentType",
      sorter: (a, b) =>
        String(a.instrumentType || "").localeCompare(String(b.instrumentType || "")),
    },
  ];

  // Fetch instruments
  const getSecurityInstrumentList = () => {
    setLoading(true);
    const body = { session };
    const url = `${apiConfig.GetSecurityInstrument}`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(cnt.DANGER, err?.defaultUserMessage || "Failed to load instruments");
        setLoading(false);
        return;
      }

      const mapped = (res || []).map((el) => ({
        key: el.id,
        id: el.id,
        instrumentName: el.instrumentName || "-",
        shortName: el.shortName || "-",
        instrumentType: el.instrumentType?.text || "-",
        raw: el,
      }));

      setSecurityList(mapped);
      setOriginalList(mapped);
      setLoading(false);
    });
  };

  useEffect(() => {
    BOM.HasPermission("READ_FINANCIAL_INSTRUMENT") && getSecurityInstrumentList();
    BOM.HasPermission("CREATE_FINANCIAL_INSTRUMENT") && rightAction(cnt.RIGHT_ADD_BTN);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search handler
  function handleSearchInTable(e) {
    setSearchTerm(e.target.value || "");
    setCurrentPage(1);
  }

  // Filtered & paginated data
  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;
    const q = searchTerm.toLowerCase();
    return originalList.filter(
      (item) =>
        (item.instrumentName || "").toLowerCase().includes(q) ||
        (item.shortName || "").toLowerCase().includes(q) ||
        (item.instrumentType || "").toLowerCase().includes(q)
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  // CSV export
  const getCsvListData = () =>
    new Promise((resolve, reject) => {
      try {
        const csv = (originalList || []).map((item) => ({
          instrumentName: item.instrumentName,
          shortName: item.shortName,
          instrumentType: item.instrumentType,
        }));
        resolve(csv);
      } catch (e) {
        reject(e);
      }
    });

  // Row selection handler (checkbox selection to match Treasury UI)
  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    // selectedRowKeys: array of keys
    // selectedRows: array of row objects
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });

      // keep add button active when allowed
      BOM.HasPermission("CREATE_FINANCIAL_INSTRUMENT") &&
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      return;
    }

    setSelectedRow(selectedRows);
    const single = selectedRows[0];

    // enable detail/edit according to permissions
    if (BOM.HasPermission("READ_FINANCIAL_INSTRUMENT")) {
      dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    }
    if (BOM.HasPermission("UPDATE_FINANCIAL_INSTRUMENT")) {
      dispatch({ type: cnt.RIGHT_EDIT_BTN });
    }
  };

  // View / Create / Edit handlers (right-sidebar)
  function handleOnView() {
    const { detailBtn } = rightBtn || {};
    if (detailBtn?.toLowerCase() === "primary") {
      navigate("/view-security-instrument", { state: selectedRow[0]?.id });
    } else {
      console.log("Please choose a record from the table to open");
    }
  }

  function handleOnCreate() {
    const { addBtn } = rightBtn || {};
    if (addBtn?.toLowerCase() === "primary") {
      navigate("/add-security-instrument", { state: null });
    } else {
      console.log("Could not open the link");
    }
  }

  function handleOnEdit() {
    const { editBtn } = rightBtn || {};
    if (editBtn?.toLowerCase() === "primary") {
      if (!selectedRow || selectedRow.length !== 1) {
        console.log("Please choose a record from the table to open");
        return;
      }
      navigate("/edit-security-instrument", { state: selectedRow[0].id });
    } else {
      console.log("Please choose a record from the table to open");
    }
  }

  // Pagination itemRender to hide prev/next icons (matches treasury UI)
  const itemRender = (_, type, originalElement) => {
    if (type === "prev") return;
    if (type === "next") return;
    return originalElement;
  };

  return (
    <MainLayout
      isEnableRightSidebar={true}
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.manageSecurityInstrument}
    >
      <TopContent pageTitle={labelConfig.securityInstrument} createPageTitle={labelConfig.productSecurityInstrument} />

      <Card
        style={{
          border: "1px solid #EAECF0",
          borderRadius: 20,
          padding: 0,
          marginTop: 12,
        }}
      >
        <Spin spinning={loading} size="large">
          <Row style={{ marginBottom: 20 }}>
            <Row>
              <Form form={form} onFinish={() => {}}>
                <Space.Compact direction="horizontal" style={{ width: "100%" }}>
                  <Form.Item style={{ margin: 0 }} name="searchKey">
                    <Input
                      value={searchTerm}
                      onChange={handleSearchInTable}
                      placeholder="Search by Name"
                      prefix={<SearchLg width={18} color="#667085" />}
                      style={{
                        width: 400,
                        borderRadius: "10px",
                      }}
                    />
                  </Form.Item>
                </Space.Compact>
              </Form>
            </Row>

            <DownloadTableButtons
              downloadData={originalList}
              columns={columns}
              fileName={"Security Instrument List"}
              onExportClick={getCsvListData}
            />
          </Row>

          <div className="treasury-bills-table-container">
            <Table
              className="bond-table"
              rowSelection={{
                type: "checkbox",
                onChange: onSelectItemAction,
                hideSelectAll: true,
              }}
              columns={columns}
              dataSource={paginatedData}
              pagination={false}
            />
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 20,
            }}
          >
            <Button
              style={{
                backgroundColor: "white",
                borderRadius: 8,
                border: "1px solid #D0D5DD",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "600",
              }}
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ArrowLeft style={{ width: "20px", height: "20px" }} /> Previous
            </Button>

            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              onChange={(page) => setCurrentPage(page)}
              showSizeChanger={false}
              itemRender={itemRender}
              align="center"
            />

            <Button
              style={{
                backgroundColor: "white",
                borderRadius: 8,
                border: "1px solid #D0D5DD",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "600",
              }}
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage >= totalPages}
            >
              Next <ArrowRight style={{ width: "20px", height: "20px" }} />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({
  userData: state.AuthenticationData.userData,
  rightBtn: state.RightSideBtn,
});

const mapDispatchToProps = { rightAction };

export default connect(mapStateToProps, mapDispatchToProps)(ManageSecurityInstruction);
